import Vue from 'vue';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import { convertAdminRoleToFlag } from '@/vuex/modules/auth/auth.helpers';
import {
    bulkDeleteUsers,
    createUser,
    fetchUser,
    fetchUsers,
    updateUser,
    updateFeedbackVendorUserForUser,
    updateUserFlag,
    updateUserFeatureFlag,
    updateAdoptManagerFlagAsSuper,
} from './users.utils';

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        inviteStatus: null,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { user }) {
            Vue.set(state.map, user.id, user);
        },
        deleteMapAtKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setInviteStatus (state, { inviteStatus }) {
            state.inviteStatus = inviteStatus;
        },
    },
    actions: {
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const userList = await fetchUsers();
                commit('setMap', { map: keyBy(userList, 'id') });
            } catch (error) {
                commit('setError', { error });
            }

            commit('setFetching', { fetching: false });
        },
        // Load single user
        async loadOne ({ commit, state }, { reFetch = false, id, permissionSets = false } = {}) {
            // Check if loaded in state unless force reFetch is true
            if (state.map[id] && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            try {
                const user = await fetchUser({ id, permissionSets });
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { user });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async invite ({ commit }, user) {
            try {
                const { status } = await createUser(user);
                if (status === 202) {
                    commit('setInviteStatus', { inviteStatus: 'sent' });
                } else {
                    commit('setInviteStatus', { inviteStatus: 'added' });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async remove ({ commit }, { id }) {
            commit('deleteMapAtKey', { id });
        },
        async bulkRemove ({ commit }, userIds) {
            const deletedUsers = await bulkDeleteUsers(userIds);

            if (deletedUsers.length === 0) {
                // 204 no content, delete all
                for (const id of userIds) {
                    commit('deleteMapAtKey', { id });
                }
            } else {
                // 207 partial delete, delete only the users that were deleted
                for (const user of deletedUsers) {
                    commit('deleteMapAtKey', { id: user.id });
                }
            }

            return deletedUsers;
        },
        async update ({ commit }, { user: userToUpdate }) {
            commit('setFetching', { fetching: true });

            try {
                // sending applicationRoles indicates this is a v2 request
                // and we DO NOT want to set flags in that case
                const user = userToUpdate.applicationRoles ? userToUpdate : convertAdminRoleToFlag(userToUpdate);

                const updatedUser = await updateUser(user);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { user: updatedUser });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFlag ({ commit, dispatch, rootGetters }, { queryParams, setValue, id, subscriptionId }) {
            const restrictedFlags = ['systemUser'];

            if (includes(restrictedFlags, queryParams.name)) {
                return;
            }

            commit('setFetching', { fetching: true });
            try {
                if (queryParams.name === 'adoptManager' && rootGetters['auth/isSuper']) {
                    await updateAdoptManagerFlagAsSuper(id, setValue, subscriptionId);
                } else {
                    await updateUserFlag(id, {
                        flagName: queryParams.name,
                        value: setValue,
                    });
                }
                commit('setFetching', { fetching: false });

                await dispatch('loadOne', { id, reFetch: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFeatureFlag ({ commit, dispatch }, { queryParams, setValue, id }) {
            commit('setFetching', { fetching: true });
            try {
                await updateUserFeatureFlag(id, {
                    flagName: queryParams.name,
                    value: setValue,
                });
                commit('setFetching', { fetching: false });

                await dispatch('loadOne', { id, reFetch: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFlags ({ commit, dispatch }, { user, flags }) {
            commit('setFetching', { fetching: true });

            try {
                const { falseRequests, trueRequests, adminChangeRequest } = Object.entries(flags).reduce(
                    (acc, flagPair) => {
                        const [flagName, flagValue] = flagPair;
                        if (flagName === 'systemUser') return acc;
                        if (flagName === 'admin') {
                            acc.adminChangeRequest = {
                                flagName,
                                value: flagValue,
                            };

                            return acc;
                        }

                        if (flagValue === true) {
                            acc.trueRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        if (flagValue === false) {
                            acc.falseRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        return acc;
                    },
                    {
                        adminChangeRequest: null,
                        falseRequests: [],
                        trueRequests: [],
                    },
                );

                // If we're going to admin, do it first so certain flag combinations are allowed
                if (adminChangeRequest && adminChangeRequest.value === true) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
                await Promise.all(falseRequests.map((falseRequestPair) => updateUserFlag(user.id, falseRequestPair)));
                await Promise.all(trueRequests.map((trueRequestsPair) => updateUserFlag(user.id, trueRequestsPair)));
                // If we're going from admin, do it last after we removed disallowed certain flag combinations
                if (adminChangeRequest && adminChangeRequest.value === false) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }

            await dispatch('loadOne', { id: user.id, reFetch: true });
        },
        async updateVendorUserForUser ({ commit, dispatch }, { userId, vendorUserPermissionId, first, last }) {
            commit('setFetching', { fetching: true });

            try {
                await updateFeedbackVendorUserForUser({
                    userId,
                    vendorUserPermissionId,
                    first,
                    last,
                });
                await dispatch('loadOne', { id: userId, reFetch: true });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        userById: (state) => (id) => state.map[id],
        userByUsername: (state, getters) => (username) => {
            return getters.list.find((user) => user.username.toLowerCase().trim() === username.toLowerCase().trim());
        },
    },
};
